// The Clay engine's read, in words: the OB Engine's tier assessment and Signal Sourcing's
// summary, turned into the short lines the Research feed shows. Pure, so `node --test` covers it.
//
// --- Why the tier reason is not rendered as a trace ---------------------------------------
//
// `Clay_Engine_Tier_Reason__c` is a six-segment scoring trace written for whoever tunes the
// engine, not for the person reading this panel. Every value written so far has this shape:
//
//     voice_shipped | evidence=product_page | ships=yes | base 1 -> Tier 1
//       | signal present but not high intent - no promotion | no cap
//
// Rendering it, in any layout, is wrong for three separate reasons: the first three segments
// restate the typed columns, the fourth restates the tier band, and the last two describe
// things that did not happen. So the verdict is written here as a sentence from the typed
// columns, and the trace is read for one thing only: the two adjustments, *when they actually
// happened*. Anything in the trace this file does not recognise still surfaces, quietly, so a
// new scoring step announces itself rather than going missing.
import type { BookAccount, ClayEngine, ClaySighting, ClaySignal } from '../api/types.ts';
import { formatCount, humanValue } from '../lib/format.ts';

// The band, said once. `tier_label` is preferred because it is the human name, and it lives in
// the hand-rolled `Clay_Metadata__c` JSON -- which can fail to parse and leave only the bare
// number behind, so the fallback builds the same label rather than printing "2" alone.
export function tierBand(engine: ClayEngine): string | null {
  if (engine.tier_label) return engine.tier_label;
  return engine.tier ? `Tier ${engine.tier}` : null;
}

// Both maps fall back to the humanised token, so a new enum value the engine starts writing
// reads as "ships a voice product, found in a press release" rather than disappearing.
const VOICE_PHRASE: Record<string, string> = {
  voice_shipped: 'Ships a voice product',
  weak: 'No clear voice product',
};

// Where the engine found its evidence, each said as a phrase. `service_offering` deliberately
// does not mention selling to others: `sells_to_others` gets its own sentence.
const EVIDENCE_PHRASE: Record<string, string> = {
  product_page: 'found on a page describing the product',
  company_description: 'found only in their own company description',
  service_offering: 'found in one of their own service offerings',
};

/** "Ships a voice product, found on a page describing the product." from the typed columns. */
export function verdictSentence(engine: ClayEngine): string {
  const voice = engine.voice_status
    ? VOICE_PHRASE[engine.voice_status] ?? `Voice status: ${humanValue(engine.voice_status)}`
    : null;
  const evidence = engine.evidence_type
    ? EVIDENCE_PHRASE[engine.evidence_type] ?? `found in ${humanValue(engine.evidence_type)}`
    : null;

  const parts: string[] = [];
  if (voice && evidence) parts.push(`${voice}, ${evidence}.`);
  else if (voice) parts.push(`${voice}.`);
  else if (evidence) parts.push(`Evidence ${evidence}.`);

  // The one fact that most often explains a low tier: a company selling voice to others is a
  // competitor or a reseller rather than somebody who would buy it for themselves.
  if (String(engine.sells_to_others).toLowerCase() === 'true') {
    parts.push('Sells voice to other companies rather than using it itself.');
  }
  return parts.join(' ');
}

export interface Adjustment { kind: string | null; text: string }

/** The two score adjustments, and only where one actually happened. A real cap carries its own
 * written reason, which is kept close to verbatim. */
export function tierAdjustments(reason: string): Adjustment[] {
  const known = [
    /^voice_shipped$/i, /^weak$/i, /^strong$/i,
    /^evidence=/i, /^ships=/i,
    /^base\s+\d+\s*->\s*tier\s+\d+$/i,
  ];
  const adjustments: Adjustment[] = [];
  String(reason).split('|').map(part => part.trim()).forEach(part => {
    if (!part) return;
    // Already said by the verdict sentence or the tier band.
    if (known.some(pattern => pattern.test(part))) return;
    // The engine declining to adjust the score is not news.
    if (/\bno\s+(cap|promotion)\b/i.test(part)) return;

    if (/cap\b/i.test(part)) adjustments.push({ kind: 'Held down', text: part });
    else if (/promot/i.test(part)) adjustments.push({ kind: 'Promoted', text: part });
    else adjustments.push({ kind: null, text: part });
  });
  return adjustments;
}

/** Whatever the signal summary says beyond dates, counts, already-linked URLs and the signal's
 * own name -- which is what an engine-seeded signal writes. */
export function summaryParts(summary: string, sightings: ClaySighting[], signalName: string | undefined): string[] {
  const linked = new Set(sightings.map(sighting => sighting.url).filter(Boolean));
  return String(summary).split('|').map(part => part.trim()).filter(part => {
    if (!part) return false;
    if (part.toLowerCase() === String(signalName ?? '').toLowerCase()) return false;
    if (/^(first|last)\s+seen\b/i.test(part)) return false;
    if (/^seen\s+\d{4}-\d{2}-\d{2}$/i.test(part)) return false;
    if (/^\d+\s+occurrences?$/i.test(part)) return false;
    if (/^https?:\/\/\S+$/.test(part)) return !linked.has(part);
    return true;
  });
}

/** Every distinct signal type present, not just the one `signal.name` holds: an account with a
 * job posting *and* a web-intent sighting reports both. */
export function signalTypes(signal: ClaySignal): string[] {
  const types = new Set<string>();
  if (signal.name) types.add(signal.name);
  (signal.sightings ?? []).forEach(sighting => {
    if (sighting.signal) types.add(sighting.signal);
  });
  return [...types];
}

/** "3 sightings, 2026-08-21 – 2026-08-25". The workflow's own tally can exceed the sightings it
 * wrote out, so the larger wins. Null when there is neither a count nor a date. */
export function signalRange(signal: ClaySignal): string | null {
  const sightingCount = Math.max(signal.count ?? 0, (signal.sightings ?? []).length);
  const countText = sightingCount ? `${formatCount(sightingCount)} sighting${sightingCount === 1 ? '' : 's'}` : null;

  // One day when first and last agree or only one is known.
  const first = signal.first_seen;
  const last = signal.last_seen;
  const rangeText = first && last && first !== last ? `${first} – ${last}` : first || last || null;
  return [countText, rangeText].filter(Boolean).join(', ') || null;
}

// What the engine knows about the company itself, for the top of the panel. On the accounts it
// has assessed the engine is often the *only* source of size, industry, country and LinkedIn, so
// these feed the header and its identity strip.
export function engineFacts(row: BookAccount) {
  const engine = row.clay?.engine ?? {};
  return {
    linkedin: engine.company_linkedin ?? null,
    employees: engine.employees ?? null,
    industry: engine.industry ?? null,
    country: engine.country ?? null,
  };
}
